import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Card, Form, Table, Badge, Alert } from 'react-bootstrap';

/**
 * CardioOverridesEditor Component
 *
 * A modal for editing cardio parameter overrides for program workouts.
 * Allows users to reuse a single template (e.g., "Long Run") with different
 * distances for each program workout instead of creating duplicate templates.
 *
 * @param {boolean} show - Whether to show the modal
 * @param {function} onHide - Handler for closing the modal
 * @param {object} template - The workout template with exercises
 * @param {object} existingOverrides - Existing overrides (if editing)
 * @param {function} onSave - Handler for saving overrides
 */
function CardioOverridesEditor({ show, onHide, template, existingOverrides, onSave }) {
  const [overrides, setOverrides] = useState({});
  const [error, setError] = useState(null);
  const [durationStrings, setDurationStrings] = useState({}); // Track raw duration input strings

  // Initialize overrides from template and existing overrides
  useEffect(() => {
    if (!template || !template.exercises) {
      setOverrides({});
      setDurationStrings({});
      return;
    }

    const initialOverrides = {};
    const initialDurationStrings = {};

    template.exercises.forEach(ex => {
      if (ex.exercise_type === 'cardio_basic' || ex.exercise_type === 'cardio_segments') {
        const overrideKey = `exercise_id_${ex.exercise_id}`;

        // Check if there's an existing override
        if (existingOverrides && existingOverrides[overrideKey]) {
          initialOverrides[overrideKey] = { ...existingOverrides[overrideKey] };

          // Initialize duration string from existing override for basic cardio
          if (ex.exercise_type === 'cardio_basic' && existingOverrides[overrideKey].duration_seconds) {
            initialDurationStrings[overrideKey] = secondsToTimeString(existingOverrides[overrideKey].duration_seconds);
          }

          // Initialize duration strings for segments
          if (ex.exercise_type === 'cardio_segments' && existingOverrides[overrideKey].segments) {
            existingOverrides[overrideKey].segments.forEach((seg, idx) => {
              if (seg.duration_seconds) {
                const segmentKey = `exercise_id_${ex.exercise_id}_segment_${idx}`;
                initialDurationStrings[segmentKey] = secondsToTimeString(seg.duration_seconds);
              }
            });
          }
        }
      }
    });

    setOverrides(initialOverrides);
    setDurationStrings(initialDurationStrings);
  }, [template, existingOverrides]);

  // Get cardio exercises from template
  const cardioExercises = template?.exercises?.filter(ex =>
    ex.exercise_type === 'cardio_basic' || ex.exercise_type === 'cardio_segments'
  ) || [];

  // Check if template has any cardio exercises
  if (cardioExercises.length === 0) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Override Cardio Parameters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <i className="fas fa-info-circle me-2"></i>
            This template does not contain any cardio exercises to override.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Convert seconds to HH:MM:SS format
  const secondsToTimeString = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Convert HH:MM:SS format to seconds
  const timeStringToSeconds = (timeString) => {
    if (!timeString) return null;
    const parts = timeString.split(':').map(p => parseInt(p) || 0);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    return null;
  };

  // Check if exercise has an override
  const hasOverride = (exerciseId) => {
    const overrideKey = `exercise_id_${exerciseId}`;
    return overrides[overrideKey] !== undefined;
  };

  // Get template default values for display
  const getTemplateDefaults = (exercise) => {
    if (exercise.exercise_type === 'cardio_basic') {
      return {
        distance: exercise.cardio_data?.distance || '',
        distance_unit: exercise.cardio_data?.distance_unit || 'miles',
        duration_seconds: exercise.cardio_data?.duration_seconds || null,
        notes: exercise.cardio_data?.notes || ''
      };
    } else if (exercise.exercise_type === 'cardio_segments') {
      return {
        segments: exercise.cardio_segments || []
      };
    }
    return null;
  };

  // Get current override values or template defaults
  const getCurrentValues = (exercise) => {
    const overrideKey = `exercise_id_${exercise.exercise_id}`;
    if (overrides[overrideKey]) {
      return overrides[overrideKey];
    }
    return null;
  };

  // Update override for basic cardio
  const updateBasicCardioOverride = (exerciseId, field, value) => {
    const overrideKey = `exercise_id_${exerciseId}`;
    const exercise = cardioExercises.find(ex => ex.exercise_id === exerciseId);

    setOverrides(prev => {
      const current = prev[overrideKey] || {
        exercise_id: exerciseId,
        exercise_type: 'cardio_basic',
        distance: exercise.cardio_data?.distance || null,
        distance_unit: exercise.cardio_data?.distance_unit || 'miles',
        duration_seconds: exercise.cardio_data?.duration_seconds || null,
        notes: exercise.cardio_data?.notes || null
      };

      return {
        ...prev,
        [overrideKey]: {
          ...current,
          [field]: value
        }
      };
    });
  };

  // Update override for segmented cardio
  const updateSegmentedCardioOverride = (exerciseId, segments) => {
    const overrideKey = `exercise_id_${exerciseId}`;

    setOverrides(prev => ({
      ...prev,
      [overrideKey]: {
        exercise_id: exerciseId,
        exercise_type: 'cardio_segments',
        segments: segments
      }
    }));
  };

  // Update a specific segment
  const updateSegment = (exerciseId, segmentIndex, field, value) => {
    const exercise = cardioExercises.find(ex => ex.exercise_id === exerciseId);
    const overrideKey = `exercise_id_${exerciseId}`;
    const currentOverride = overrides[overrideKey];

    // Get current segments (from override or template)
    let segments = currentOverride?.segments || exercise.cardio_segments || [];

    // Make a copy and update the specific segment
    segments = segments.map((seg, idx) => {
      if (idx === segmentIndex) {
        return { ...seg, [field]: value };
      }
      return seg;
    });

    updateSegmentedCardioOverride(exerciseId, segments);
  };

  // Add a new segment
  const addSegment = (exerciseId) => {
    const exercise = cardioExercises.find(ex => ex.exercise_id === exerciseId);
    const overrideKey = `exercise_id_${exerciseId}`;
    const currentOverride = overrides[overrideKey];

    let segments = currentOverride?.segments || exercise.cardio_segments || [];
    const newSegment = {
      segment_number: segments.length + 1,
      distance: null,
      distance_unit: 'miles',
      duration_seconds: null,
      notes: ''
    };

    updateSegmentedCardioOverride(exerciseId, [...segments, newSegment]);
  };

  // Remove a segment
  const removeSegment = (exerciseId, segmentIndex) => {
    const exercise = cardioExercises.find(ex => ex.exercise_id === exerciseId);
    const overrideKey = `exercise_id_${exerciseId}`;
    const currentOverride = overrides[overrideKey];

    let segments = currentOverride?.segments || exercise.cardio_segments || [];
    segments = segments.filter((_, idx) => idx !== segmentIndex);

    // Re-number segments
    segments = segments.map((seg, idx) => ({
      ...seg,
      segment_number: idx + 1
    }));

    updateSegmentedCardioOverride(exerciseId, segments);
  };

  // Reset exercise to template defaults (remove override)
  const resetToTemplate = (exerciseId) => {
    const overrideKey = `exercise_id_${exerciseId}`;
    setOverrides(prev => {
      const updated = { ...prev };
      delete updated[overrideKey];
      return updated;
    });
  };

  // Validate overrides
  const validateOverrides = () => {
    for (const [key, override] of Object.entries(overrides)) {
      if (override.exercise_type === 'cardio_basic') {
        if (override.distance !== null && override.distance < 0) {
          return 'Distance cannot be negative';
        }
        if (override.duration_seconds !== null && override.duration_seconds < 0) {
          return 'Duration cannot be negative';
        }
      } else if (override.exercise_type === 'cardio_segments') {
        for (const segment of override.segments) {
          if (segment.distance !== null && segment.distance < 0) {
            return 'Segment distance cannot be negative';
          }
          if (segment.duration_seconds !== null && segment.duration_seconds < 0) {
            return 'Segment duration cannot be negative';
          }
        }
      }
    }
    return null;
  };

  // Handle save
  const handleSave = () => {
    const validationError = validateOverrides();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Only save non-empty overrides
    const filteredOverrides = {};
    for (const [key, override] of Object.entries(overrides)) {
      filteredOverrides[key] = override;
    }

    // If no overrides, pass null
    const finalOverrides = Object.keys(filteredOverrides).length > 0 ? filteredOverrides : null;

    onSave(finalOverrides);
    setError(null);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-running me-2"></i>
          Override Cardio Parameters
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert variant="info" className="mb-4">
          <i className="fas fa-info-circle me-2"></i>
          Customize cardio parameters for this specific workout while keeping the original template unchanged.
          Leave fields empty to use template defaults.
        </Alert>

        {cardioExercises.map((exercise) => {
          const defaults = getTemplateDefaults(exercise);
          const current = getCurrentValues(exercise);
          const isOverridden = hasOverride(exercise.exercise_id);

          return (
            <Card key={exercise.exercise_id} className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{exercise.name}</strong>
                  {isOverridden && (
                    <Badge bg="warning" className="ms-2">
                      <i className="fas fa-edit me-1"></i>
                      Overridden
                    </Badge>
                  )}
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => resetToTemplate(exercise.exercise_id)}
                  disabled={!isOverridden}
                  title="Reset to template defaults"
                >
                  <i className="fas fa-undo me-1"></i>
                  Reset to Template
                </Button>
              </Card.Header>
              <Card.Body>
                {exercise.exercise_type === 'cardio_basic' ? (
                  // Basic Cardio UI
                  <div>
                    <Form.Group className="mb-3">
                      <Form.Label>Distance</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={current?.distance ?? ''}
                          onChange={(e) => updateBasicCardioOverride(
                            exercise.exercise_id,
                            'distance',
                            e.target.value ? parseFloat(e.target.value) : null
                          )}
                          placeholder={`Template: ${defaults.distance || 'Not set'}`}
                        />
                        <Form.Select
                          value={current?.distance_unit || defaults.distance_unit}
                          onChange={(e) => updateBasicCardioOverride(
                            exercise.exercise_id,
                            'distance_unit',
                            e.target.value
                          )}
                          style={{ maxWidth: '150px' }}
                        >
                          <option value="miles">Miles</option>
                          <option value="km">Kilometers</option>
                          <option value="meters">Meters</option>
                          <option value="yards">Yards</option>
                        </Form.Select>
                      </div>
                      {defaults.distance && (
                        <Form.Text className="text-muted">
                          Template default: {defaults.distance} {defaults.distance_unit}
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Duration (HH:MM:SS)</Form.Label>
                      <Form.Control
                        type="text"
                        value={
                          durationStrings[`exercise_id_${exercise.exercise_id}`] ??
                          secondsToTimeString(current?.duration_seconds ?? defaults.duration_seconds)
                        }
                        onChange={(e) => {
                          const overrideKey = `exercise_id_${exercise.exercise_id}`;
                          // Update the raw string immediately for responsive typing
                          setDurationStrings(prev => ({
                            ...prev,
                            [overrideKey]: e.target.value
                          }));
                        }}
                        onBlur={(e) => {
                          // Convert to seconds when user finishes typing
                          const seconds = timeStringToSeconds(e.target.value);
                          updateBasicCardioOverride(
                            exercise.exercise_id,
                            'duration_seconds',
                            seconds
                          );
                        }}
                        placeholder="00:30:00"
                      />
                      {defaults.duration_seconds && (
                        <Form.Text className="text-muted">
                          Template default: {secondsToTimeString(defaults.duration_seconds)}
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={current?.notes ?? ''}
                        onChange={(e) => updateBasicCardioOverride(
                          exercise.exercise_id,
                          'notes',
                          e.target.value
                        )}
                        placeholder={defaults.notes || 'Add notes...'}
                      />
                    </Form.Group>
                  </div>
                ) : (
                  // Segmented Cardio UI
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Segments</h6>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => addSegment(exercise.exercise_id)}
                      >
                        <i className="fas fa-plus me-1"></i>
                        Add Segment
                      </Button>
                    </div>

                    <Table responsive bordered size="sm">
                      <thead>
                        <tr>
                          <th width="10%">#</th>
                          <th width="30%">Distance</th>
                          <th width="25%">Duration</th>
                          <th width="25%">Notes</th>
                          <th width="10%">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(current?.segments || defaults.segments).map((segment, idx) => {
                          const templateSegment = defaults.segments[idx];
                          return (
                            <tr key={idx}>
                              <td className="text-center">{segment.segment_number}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    size="sm"
                                    value={segment.distance ?? ''}
                                    onChange={(e) => updateSegment(
                                      exercise.exercise_id,
                                      idx,
                                      'distance',
                                      e.target.value ? parseFloat(e.target.value) : null
                                    )}
                                    placeholder={templateSegment?.distance?.toString() || '0'}
                                  />
                                  <Form.Select
                                    size="sm"
                                    value={segment.distance_unit || 'miles'}
                                    onChange={(e) => updateSegment(
                                      exercise.exercise_id,
                                      idx,
                                      'distance_unit',
                                      e.target.value
                                    )}
                                    style={{ maxWidth: '80px' }}
                                  >
                                    <option value="miles">mi</option>
                                    <option value="km">km</option>
                                    <option value="meters">m</option>
                                    <option value="yards">yd</option>
                                  </Form.Select>
                                </div>
                              </td>
                              <td>
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  value={
                                    durationStrings[`exercise_id_${exercise.exercise_id}_segment_${idx}`] ??
                                    secondsToTimeString(segment.duration_seconds)
                                  }
                                  onChange={(e) => {
                                    const durationKey = `exercise_id_${exercise.exercise_id}_segment_${idx}`;
                                    // Update the raw string immediately for responsive typing
                                    setDurationStrings(prev => ({
                                      ...prev,
                                      [durationKey]: e.target.value
                                    }));
                                  }}
                                  onBlur={(e) => {
                                    // Convert to seconds when user finishes typing
                                    const seconds = timeStringToSeconds(e.target.value);
                                    updateSegment(
                                      exercise.exercise_id,
                                      idx,
                                      'duration_seconds',
                                      seconds
                                    );
                                  }}
                                  placeholder={templateSegment?.duration_seconds
                                    ? secondsToTimeString(templateSegment.duration_seconds)
                                    : '00:00:00'
                                  }
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  value={segment.notes || ''}
                                  onChange={(e) => updateSegment(
                                    exercise.exercise_id,
                                    idx,
                                    'notes',
                                    e.target.value
                                  )}
                                  placeholder={templateSegment?.notes || 'Notes...'}
                                />
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeSegment(exercise.exercise_id, idx)}
                                  title="Remove segment"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    {defaults.segments.length > 0 && (
                      <Form.Text className="text-muted">
                        Template has {defaults.segments.length} segment{defaults.segments.length !== 1 ? 's' : ''}
                      </Form.Text>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          <i className="fas fa-save me-2"></i>
          Save Overrides
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CardioOverridesEditor;
